import { DecimalPipe, PercentPipe } from '@angular/common';
import { PrepackQuantityPipe } from '@epicuro-next/ataraxia/prepack-quantity-pipe';
import {
  notAvailableLabel,
  orderTypeLocale,
} from '@epicuro-next/constants/features/localization';
import {
  ORDER_COLUMN_QUANTITY_FORMAT,
  ORDER_LINE_DATE_FORMAT,
  ORDER_LINE_NUMBER_FORMAT,
} from '@epicuro-next/constants/features/order-book';
import { genderIcons } from '@epicuro-next/constants/ui/icons';
import {
  IOrdersSettings,
  OrderItem,
  OrderMaterial,
  PrepackInfo,
} from '@epicuro-next/models/order-book';
import {
  formatDateToLocaleDateUtc,
  getISODate,
  isNullish,
  utcDateTime,
} from '@epicuro-next/utilities';
import { OrderType } from 'graphql-api/baskets-api-interfaces';

export function getGenderValue(gender: string) {
  if (!gender) return {};
  return genderIcons[gender as 'Mens' | 'Womens' | 'Unisex'] ?? {};
}

export function getDateValue(
  value: string | null,
  locale: string,
  format?: string,
): string {
  return (
    (value &&
      formatDateToLocaleDateUtc(
        utcDateTime(value).toISODate(),
        locale,
        format ? format : ORDER_LINE_DATE_FORMAT,
      )) ||
    notAvailableLabel.toUpperCase()
  );
}

export function getNumberValue<T>(value: number, pipe: DecimalPipe) {
  return pipe.transform(value, ORDER_LINE_NUMBER_FORMAT) as T;
}

export function getPercentValue<T>(
  value: number,
  pipe: PercentPipe,
  format: string,
) {
  return pipe.transform(value, format) as T;
}

export function getOrderQuantity(
  order: OrderMaterial,
  pipe: PrepackQuantityPipe,
): string | number {
  if (order.prepackInfo) {
    return getPrepackQuantity(order.prepackInfo, pipe);
  }

  return order.materials
    .flatMap(({ sizes }) => sizes)
    .map(({ quantity }) => quantity)
    .reduce((sum, curr) => sum + curr, 0);
}

export function getOrderItemQuantity(
  orderItem: OrderItem,
  property: keyof Pick<
    OrderItem,
    'requestedQuantity' | 'confirmedQuantity' | 'unconfirmedQuantity'
  >,
  pipe: PrepackQuantityPipe,
): string | number {
  if (orderItem.prepackInfo) {
    return getPrepackQuantity(orderItem.prepackInfo, pipe);
  }

  return orderItem[property];
}

export function getPrepackQuantity(
  prepackInfo: PrepackInfo,
  pipe: PrepackQuantityPipe,
) {
  return pipe.transform(
    prepackInfo.quantity * prepackInfo.size,
    prepackInfo.size,
    ORDER_COLUMN_QUANTITY_FORMAT,
  );
}

export function getOrderThumbnails(
  payload: {
    productId: string;
    brandId: string;
  },
  settings: IOrdersSettings,
): string {
  const productId = payload.productId;

  if (
    isNullish(settings?.cdnUrl) ||
    isNullish(payload.brandId) ||
    isNullish(productId)
  ) {
    return '';
  }

  const imageId = settings.defaultImageType
    ? `${payload.brandId}__${productId}__${settings.defaultImageType}.jpg`
    : `${payload.brandId}__${productId}.jpg`;

  return `${settings.cdnUrl}/images/products/${imageId}?cache=${getISODate(
    new Date(),
  )}`;
}

export function getFormattedOrderType(orderType: string) {
  return orderTypeLocale[orderType.toUpperCase() as OrderType];
}
